import React from 'react';
import { Table, Tag, Button, Card, Form, Input, Space, Typography } from 'antd';
import { SearchOutlined, CheckCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { TripItem, EmpFilter } from '../../../services/p9513Api';

const { Text } = Typography;

interface Props {
  trips: TripItem[];
  total: number;
  loading: boolean;
  approving: boolean;
  selectedKeys: string[];
  filter: EmpFilter;
  onFilterChange: (f: EmpFilter) => void;
  onSearch: () => void;
  onSelect: (keys: string[]) => void;
  onApprove: () => void;
}

function renderVPStat(stat: string): React.ReactNode {
  if (!stat) return <Tag color="warning">待審</Tag>;
  if (stat === 'Y') return <Tag color="success">已核准</Tag>;
  if (stat === 'N') return <Tag color="error">已拒絕</Tag>;
  if (stat === 'P') return <Tag color="blue">代理核准</Tag>;
  return <Tag>{stat}</Tag>;
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('zh-TW');
}

const columns: ColumnsType<TripItem> = [
  { title: '出差單號', dataIndex: 'trip_no', key: 'trip_no', width: 110, render: (t) => <Text code>{t}</Text> },
  { title: '員工編號', dataIndex: 'emp_no', key: 'emp_no', width: 90 },
  { title: '員工姓名', dataIndex: 'emp_name', key: 'emp_name', width: 90 },
  { title: '部門', dataIndex: 'dept_code', key: 'dept_code', width: 80 },
  { title: '出差地點', dataIndex: 'dest', key: 'dest', width: 120 },
  { title: '開始日期', dataIndex: 'start_date', key: 'start_date', width: 100, render: formatDate },
  { title: '結束日期', dataIndex: 'end_date', key: 'end_date', width: 100, render: formatDate },
  { title: '副總狀態', dataIndex: 'vp_stat', key: 'vp_stat', width: 90, align: 'center', render: renderVPStat },
  { title: '出差目的', dataIndex: 'purpose', key: 'purpose', ellipsis: true },
];

const TripGrid: React.FC<Props> = ({
  trips, total, loading, approving, selectedKeys,
  filter, onFilterChange, onSearch, onSelect, onApprove,
}) => {
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => onSelect(keys as string[]),
  };

  return (
    <>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label="員工編號起始">
            <Input
              value={filter.empStart}
              onChange={(e) => onFilterChange({ ...filter, empStart: e.target.value })}
              placeholder="如：00001"
              style={{ width: 100 }}
              onPressEnter={onSearch}
            />
          </Form.Item>
          <Form.Item label="員工編號結束">
            <Input
              value={filter.empEnd}
              onChange={(e) => onFilterChange({ ...filter, empEnd: e.target.value })}
              placeholder="如：99999"
              style={{ width: 100 }}
              onPressEnter={onSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<SearchOutlined />} onClick={onSearch} loading={loading}>
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 8 }}>
        <Space style={{ justifyContent: 'space-between', width: '100%', display: 'flex' }}>
          <Text type="secondary">
            共 <strong>{total}</strong> 筆待審，已選 <strong>{selectedKeys.length}</strong> 筆
          </Text>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={approving}
            disabled={selectedKeys.length === 0}
            onClick={onApprove}
          >
            批量處理
          </Button>
        </Space>
      </Card>

      <Table<TripItem>
        rowKey="trip_no"
        columns={columns}
        dataSource={trips}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (t) => `共 ${t} 筆` }}
        scroll={{ x: 900 }}
        locale={{ emptyText: '無待核准出差單' }}
      />
    </>
  );
};

export default TripGrid;
